<template>
    <div>
        <app-modal 
            :show="show && path === 'sq-by-address'" 
            @close="close"
            :width="addresses.length == 0 ? 'md:w-1/2 lg:w-1/3' : 'lg:w-1/2'"
            bg="bg-gray-200"
        >
            <template slot="header">SQ By Address</template>
            <template slot="body">
                <template v-if="addresses.length == 0">
                    <div class="mb-5">To complete a service qualification, please provide an address below. If you know of the CSN/ULL ID for the location below, please provide it as part of the address search.</div>
                    <div class="card">
                        <div class="card-body">
                            <div 
                                class="bg-red-500 px-3 py-2 rounded text-sm shadow text-white mb-5"
                                v-if="searchAddressForm.errors.has('results')"
                            >
                                No results found for <strong>"{{ searchAddressForm.address }}"</strong>
                            </div>
                            <app-input
                                v-model="searchAddressForm.address"
                                placeholder="Service Address"
                                :error="searchAddressForm.errors.get('address')"
                                @updated="() => { 
                                    searchAddressForm.errors.clear('address')
                                    searchAddressForm.errors.clear('results')
                                }"
                                @keyup.enter.native="searchAddress"
                                focus
                            />
                            <app-input
                                v-model="searchAddressForm.potsInterconnectId"
                                placeholder="CSN/ULL ID"
                                :error="searchAddressForm.errors.get('potsInterconnectId')"
                                @updated="searchAddressForm.errors.clear('potsInterconnectId')"
                            />
                            <div class="mt-5 text-right">
                                <app-button 
                                    class="btn btn-blue"
                                    @click="searchAddress"
                                    :loading="searchingAddress"
                                >SEARCH</app-button>
                            </div>
                        </div>
                    </div>                    
                </template>
                <template v-else-if="cantFindAddress">
                    <div class="mb-5">To complete an advanced address service qualification, please complete the form below. Some information may already be provided.</div>
                    <div class="card">
                        <div class="card-body">
                            <h2 class="uppercase tracking-widest">Basic Address Details</h2>
                            <div class="flex flex-wrap -mx-4">
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.roadnumber1"
                                        placeholder="Road Number"
                                        :error="advancedSearchForm.errors.get('roadnumber1')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('roadnumber1')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                        focus
                                    />
                                </div>
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.roadnumber2"
                                        placeholder="Road Number 2 (eg. 120 if 80-120)"
                                        :error="advancedSearchForm.errors.get('roadnumber2')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('roadnumber2')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>
                            </div>
                            <div class="flex flex-wrap -mx-4">
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.roadname"
                                        placeholder="Road Name"
                                        :error="advancedSearchForm.errors.get('roadname')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('roadname')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>
                                <div class="w-full md:w-1/2 px-4">
                                    <app-select
                                        v-model="advancedSearchForm.roadtypecode"
                                        placeholder="Road Type Code"
                                        :error="advancedSearchForm.errors.get('roadtypecode')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('roadtypecode')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        :options="['AVENUE','DRIVE','ROAD','STREET','ACCESS','ACRE','ALLEY','ALLEYWAY','AMBLE','APPROACH','ARCADE','ARTERIAL','ARTERY','AWLK','BANAN','BANK','BAY','BEACH','BEND','BOARDWALK','BOULEVARD','BOULEVARDE','BOWL','BRACE','BRAE','BRANCH','BREAK','BRETT','BRIDGE','BROADWALK','BROADWAY','BROW','BULL','BUSWAY','BYPASS','BYWAY','CAUSEWAY','CENTRE','CENTREWAY','CHASE','CIRCLE','CIRCLET','CIRCUIT','CIRCUS','CLOSE','CLUSTER','COLONNADE','COMMON','COMMONS','CONCORD','CONCOURSE','CONNECTION','COPSE','CORNER','CORSO','COURSE','COURT','COURTYARD','COVE','CRESCENT','CREST','CRIEF','CROOK','CROSS','CROSSING','CRUISEWAY','CUL-DE-SAC','CUT','CUTTING','DALE','DASH','DELL','DENE','DEVIATION','DIP','DISTRIBUTOR','DIVIDE','DOCK','DOMAIN','DOWN','DOWNS','DRIVEWAY','EASEMENT','EAST','EDGE','ELBOW','END','ENTRANCE','ESPLANADE','ESTATE','EXPRESSWAY','EXTENSION','FAIRWAY','FIREBREAK','FIRELINE','FIRETRACK','FIRETRAIL','FLAT','FLATS','FOLLOW','FOOTWAY','FORD','FORESHORE','FORK','FORMATION','FREEWAY','FRONT','FRONTAGE','GAP','GARDEN','GARDENS','GATE','GATEWAY','GLADE','GLEN','GRANGE','GREEN','GROVE','GULLY','HARBOUR','HAVEN','HEATH','HEIGHTS','HIGHROAD','HIGHWAY','HIKE','HILL','HILLS','HOLLOW','HUB','INLET','INTERCHANGE','ISLAND','JUNCTION','KEY','KEYS','KNOLL','LADDER','LANDING','LANE','LANEWAY','LEAD','LEADER','LINE','LINK','LOOKOUT','LOOP','LYNNE','MALL','MANOR','MART','MAZE','MEAD','MEANDER','MEW','MEWS','MILE','MOTORWAY','NOOK','NORTH','NULL','OUTLET','OUTLOOK','OVAL','PALMS','PARADE','PARADISE','PARK','PARKWAY','PART','PASS','PASSAGE','PATH','PATHWAY','PENINSULA','PIAZZA','PLACE','PLAZA','POCKET','POINT','PORT','PRECINCT','PROMENADE','PURSUIT','QUAD','QUADRANT','QUAY','QUAYS','RAMBLE','RAMP','RANGE','REACH','REEF','RESERVE','REST','RETREAT','RETURN','RIDE','RIDGE','RIGHT OF WAY','RING','RISE','RISING','RIVER','ROADS','ROADWAY','ROTARY','ROUND','ROUTE','ROW','ROWE','RUE','RUN','SERVICEWAY','SHUNT','SKYLINE','SLOPE','SOUTH','SPUR','SQUARE','STEPS','STRAIGHT','STRAIT','STRAND','STRIP','SUBWAY','TARN','TERRACE','THOROUGHFARE','THROUGHWAY','TOLLWAY','TOP','TOR','TRACK','TRAIL','TRAMWAY','TRAVERSE','TRUNKWAY','TUNNEL','TURN','TWIST','UNDERPASS','VALE','VALLEY','VERGE','VIADUCT','VIEW','VIEWS','VILLA','VILLAGE','VILLAS','VISTA','VUE','WADE','WALK','WALKWAY','WATERS','WATERWAY','WAY','WEST','WHARF','WOOD','WOODS','WYND','YARD',]"
                                    />
                                </div>
                            </div>
                            <app-select
                                v-model="advancedSearchForm.roadsuffixcode"
                                placeholder="Road Suffix Code"
                                :error="advancedSearchForm.errors.get('roadsuffixcode')"
                                @updated="() => { 
                                    advancedSearchForm.errors.clear('roadsuffixcode')
                                    advancedSearchForm.errors.clear('results')
                                }"
                                :options="['CENTRAL','CROSSOVER','DEVIATION','EAST','EXTENSION','INNER','LOWER','MALL','NORTH','NORTH EAST','NORTH WEST','OFF','ON','OVERPASS','OUTER','SOUTH','SOUTH EAST','SOUTH WEST','UPPER','WEST',]"
                            />
                            <div class="flex flex-wrap -mx-4">
                                <div class="w-full md:w-1/2 px-4">
                                    <app-select
                                        v-model="advancedSearchForm.unittypecode"
                                        placeholder="Unit Type Code"
                                        :error="advancedSearchForm.errors.get('unittypecode')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('unittypecode')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        :options="['APARTMENT','BUILDING','FLAT','HOUSE','LOT','ROOM','SUITE','SHOP','UNIT','ANTENNA','AUTOMATED TELLER MACHINE','BARBECUE','BLOCK','BUNGALOW','BOATSHED','CAGE','CARPARK','CARSPACE','CLUB','COOLROOM','COTTAGE','DUPLEX','FACTORY','GARAGE','HALL','KIOSK','LOBBY','LOFT','LEASE','MARINE BERTH','MAISONETTE','NON BUILDING STRUCTURE','OFFICE','PENTHOUSE','REAR','RESERVE','SECTION','SHED','SHOWROOM','SIGN','SITE','STALL','STORE','STRATA UNIT','STUDIO','SUBSTATION','TENANCY','TOWNHOUSE','TOWER','VILLA','VAULT','WARD','WAREHOUSE','WORKSHOP',]"
                                    />
                                </div>
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.unitnumber"
                                        placeholder="Unit Number"
                                        :error="advancedSearchForm.errors.get('unitnumber')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('unitnumber')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>                                
                            </div>
                            <h2 class="mt-5 uppercase tracking-widest">Additional address details that may help pinpoint a premises</h2>
                            <div class="flex flex-wrap -mx-4">
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.lotnumber"
                                        placeholder="Lot Number"
                                        :error="advancedSearchForm.errors.get('lotnumber')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('lotnumber')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.plannumber"
                                        placeholder="Plan Number"
                                        :error="advancedSearchForm.errors.get('plannumber')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('plannumber')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>                                
                            </div>
                            <app-input
                                v-model="advancedSearchForm.addresssitename"
                                placeholder="Address Site Name"
                                :error="advancedSearchForm.errors.get('addresssitename')"
                                @updated="() => { 
                                    advancedSearchForm.errors.clear('addresssitename')
                                    advancedSearchForm.errors.clear('results')
                                }"
                                @keyup.enter.native="searchAddress"
                            />
                            <div class="mt-5 mb-6">
                                <checkbox
                                    v-model="isComplex"
                                >This address is part of a multi-story building or complex.</checkbox>
                            </div>
                            <div class="mb-5" v-if="isComplex">
                                <h2 class="uppercase tracking-widest">Complex Details</h2>
                                <div class="flex flex-wrap -mx-4">
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-select
                                            v-model="advancedSearchForm.leveltypecode"
                                            placeholder="Level Type Code"
                                            :error="advancedSearchForm.errors.get('leveltypecode')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('leveltypecode')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            :options="['APARTMENT','BUILDING','FLAT','HOUSE','LOT','ROOM','SUITE','SHOP','UNIT','ANTENNA','AUTOMATED TELLER MACHINE','BARBECUE','BLOCK','BUNGALOW','BOATSHED','CAGE','CARPARK','CARSPACE','CLUB','COOLROOM','COTTAGE','DUPLEX','FACTORY','GARAGE','HALL','KIOSK','LOBBY','LOFT','LEASE','MARINE BERTH','MAISONETTE','NON BUILDING STRUCTURE','OFFICE','PENTHOUSE','REAR','RESERVE','SECTION','SHED','SHOWROOM','SIGN','SITE','STALL','STORE','STRATA UNIT','STUDIO','SUBSTATION','TENANCY','TOWNHOUSE','TOWER','VILLA','VAULT','WARD','WAREHOUSE','WORKSHOP',]"
                                        />
                                    </div>
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-input
                                            v-model="advancedSearchForm.levelnumber"
                                            placeholder="Level Number"
                                            :error="advancedSearchForm.errors.get('levelnumber')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('levelnumber')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            @keyup.enter.native="searchAddress"
                                        />
                                    </div>
                                </div>
                                <app-input
                                    v-model="advancedSearchForm.secondarycomplexname"
                                    placeholder="Secondary Complex Name"
                                    :error="advancedSearchForm.errors.get('secondarycomplexname')"
                                    @updated="() => { 
                                        advancedSearchForm.errors.clear('secondarycomplexname')
                                        advancedSearchForm.errors.clear('results')
                                    }"
                                    @keyup.enter.native="searchAddress"
                                />
                                <div class="flex flex-wrap -mx-4">
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-input
                                            v-model="advancedSearchForm.complexroadenumber1"
                                            placeholder="Complex Road Number 1"
                                            :error="advancedSearchForm.errors.get('complexroadenumber1')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('complexroadenumber1')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            @keyup.enter.native="searchAddress"
                                        />
                                    </div>
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-input
                                            v-model="advancedSearchForm.complexroadnumber2"
                                            placeholder="Complex Road Number 2"
                                            :error="advancedSearchForm.errors.get('complexroadnumber2')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('complexroadnumber2')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            @keyup.enter.native="searchAddress"
                                        />
                                    </div>
                                </div>
                                <div class="flex flex-wrap -mx-4">
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-input
                                            v-model="advancedSearchForm.complexroadname"
                                            placeholder="Complex Road Name"
                                            :error="advancedSearchForm.errors.get('complexroadname')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('complexroadname')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            @keyup.enter.native="searchAddress"
                                        />
                                    </div>
                                    <div class="w-full md:w-1/2 px-4">
                                        <app-select
                                            v-model="advancedSearchForm.complexroadtypecode"
                                            placeholder="Complex Road Type Code"
                                            :error="advancedSearchForm.errors.get('complexroadtypecode')"
                                            @updated="() => { 
                                                advancedSearchForm.errors.clear('complexroadtypecode')
                                                advancedSearchForm.errors.clear('results')
                                            }"
                                            :options="['AVENUE','DRIVE','ROAD','STREET','ACCESS','ACRE','ALLEY','ALLEYWAY','AMBLE','APPROACH','ARCADE','ARTERIAL','ARTERY','AWLK','BANAN','BANK','BAY','BEACH','BEND','BOARDWALK','BOULEVARD','BOULEVARDE','BOWL','BRACE','BRAE','BRANCH','BREAK','BRETT','BRIDGE','BROADWALK','BROADWAY','BROW','BULL','BUSWAY','BYPASS','BYWAY','CAUSEWAY','CENTRE','CENTREWAY','CHASE','CIRCLE','CIRCLET','CIRCUIT','CIRCUS','CLOSE','CLUSTER','COLONNADE','COMMON','COMMONS','CONCORD','CONCOURSE','CONNECTION','COPSE','CORNER','CORSO','COURSE','COURT','COURTYARD','COVE','CRESCENT','CREST','CRIEF','CROOK','CROSS','CROSSING','CRUISEWAY','CUL-DE-SAC','CUT','CUTTING','DALE','DASH','DELL','DENE','DEVIATION','DIP','DISTRIBUTOR','DIVIDE','DOCK','DOMAIN','DOWN','DOWNS','DRIVEWAY','EASEMENT','EAST','EDGE','ELBOW','END','ENTRANCE','ESPLANADE','ESTATE','EXPRESSWAY','EXTENSION','FAIRWAY','FIREBREAK','FIRELINE','FIRETRACK','FIRETRAIL','FLAT','FLATS','FOLLOW','FOOTWAY','FORD','FORESHORE','FORK','FORMATION','FREEWAY','FRONT','FRONTAGE','GAP','GARDEN','GARDENS','GATE','GATEWAY','GLADE','GLEN','GRANGE','GREEN','GROVE','GULLY','HARBOUR','HAVEN','HEATH','HEIGHTS','HIGHROAD','HIGHWAY','HIKE','HILL','HILLS','HOLLOW','HUB','INLET','INTERCHANGE','ISLAND','JUNCTION','KEY','KEYS','KNOLL','LADDER','LANDING','LANE','LANEWAY','LEAD','LEADER','LINE','LINK','LOOKOUT','LOOP','LYNNE','MALL','MANOR','MART','MAZE','MEAD','MEANDER','MEW','MEWS','MILE','MOTORWAY','NOOK','NORTH','NULL','OUTLET','OUTLOOK','OVAL','PALMS','PARADE','PARADISE','PARK','PARKWAY','PART','PASS','PASSAGE','PATH','PATHWAY','PENINSULA','PIAZZA','PLACE','PLAZA','POCKET','POINT','PORT','PRECINCT','PROMENADE','PURSUIT','QUAD','QUADRANT','QUAY','QUAYS','RAMBLE','RAMP','RANGE','REACH','REEF','RESERVE','REST','RETREAT','RETURN','RIDE','RIDGE','RIGHT OF WAY','RING','RISE','RISING','RIVER','ROADS','ROADWAY','ROTARY','ROUND','ROUTE','ROW','ROWE','RUE','RUN','SERVICEWAY','SHUNT','SKYLINE','SLOPE','SOUTH','SPUR','SQUARE','STEPS','STRAIGHT','STRAIT','STRAND','STRIP','SUBWAY','TARN','TERRACE','THOROUGHFARE','THROUGHWAY','TOLLWAY','TOP','TOR','TRACK','TRAIL','TRAMWAY','TRAVERSE','TRUNKWAY','TUNNEL','TURN','TWIST','UNDERPASS','VALE','VALLEY','VERGE','VIADUCT','VIEW','VIEWS','VILLA','VILLAGE','VILLAS','VISTA','VUE','WADE','WALK','WALKWAY','WATERS','WATERWAY','WAY','WEST','WHARF','WOOD','WOODS','WYND','YARD',]"
                                        />
                                    </div>                                    
                                </div>
                            </div>
                            <h2 class="uppercase tracking-widest">Locality Details</h2>
                            <app-input
                                v-model="advancedSearchForm.localityname"
                                placeholder="Locality Name"
                                :error="advancedSearchForm.errors.get('localityname')"
                                @updated="() => { 
                                    advancedSearchForm.errors.clear('localityname')
                                    advancedSearchForm.errors.clear('results')
                                }"
                                @keyup.enter.native="searchAddress"
                            />
                            <div class="flex flex-wrap -mx-4">
                                <div class="w-full md:w-1/2 px-4">
                                    <app-select
                                        v-model="advancedSearchForm.stateterritorycode"
                                        placeholder="State / Territory Code"
                                        :error="advancedSearchForm.errors.get('stateterritorycode')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('stateterritorycode')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        :options="['ACT','NSW','NT','QLD','SA','TAS','VIC','WA',]"
                                    />
                                </div>
                                <div class="w-full md:w-1/2 px-4">
                                    <app-input
                                        v-model="advancedSearchForm.postcode"
                                        placeholder="Postcode"
                                        :error="advancedSearchForm.errors.get('postcode')"
                                        @updated="() => { 
                                            advancedSearchForm.errors.clear('postcode')
                                            advancedSearchForm.errors.clear('results')
                                        }"
                                        @keyup.enter.native="searchAddress"
                                    />
                                </div>                                
                            </div>
                            <div class="mt-5 text-right">
                                <app-button
                                    class="btn-blue"
                                    @click="advancedSearch"
                                    :loading="advancedSearchFormIsLoading"
                                >Find Address</app-button>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="flex items-center justify-between mb-5">
                        <div v-if="searchAddressForm.address">Results for <strong>"{{ searchAddressForm.address }}"</strong></div>
                        <a 
                            class="btn bg-white shadow text-gray-600 hover:bg-blue-500 hover:text-white flex items-center"
                            @click="() => {
                                this.$store.commit('wideband/address/setItems', []);
                                this.searchAddressForm.reset();
                            }"
                        >
                            <left-arrow-icon />
                            <span class="ml-2">Back to search</span>
                        </a>
                    </div>
                    <div class="card">
                        <table class="table">
                            <tr v-for="address in addresses">
                                <td>
                                    {{ address.address || address.formattedAddress }}<br>
                                    <span class="text-sm text-gray-600">{{ address.nbnLocationId }}</span>
                                </td>
                                <td class="text-right" width="110">
                                    <app-button 
                                        class="btn btn-blue"
                                        @click="check(address)"
                                    >Check</app-button>
                                </td>
                            </tr>
                        </table>                        
                    </div>
                    <div class="text-right mt-5">
                        <app-button 
                            class="btn-red" 
                            @click="cantFindAddress = true"
                        >Cant find address?</app-button>
                    </div>
                </template>
            </template>
        </app-modal>
        <app-modal 
            :show.sync="showResultsModal"
            width="lg:w-1/2"
            bg="bg-gray-200"
            @close="() => {
                asqError = null;
                showNtdCpError = false;
            }"
        >
            <template slot="header">SERVICE QUALIFICATION RESULTS</template>
            <template slot="body">
                <div class="mb-5">
                    Shown below are the service qualification results provided by NBNCo for the {{ address ? 'address' : 'location ID' }}: <strong class="text-sm">"{{ address || nbnLocationId }}"</strong>
                </div>
                <template v-if="asqError == null">
                    <div class="card mb-5">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">Service</h2>
                            <table class="table">
                                <tr>
                                    <td class="font-semibold" width="200">Service Class</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.serviceClassNumber }} - {{ asq.serviceClassDesc }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">NBN Location ID</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.nbnLocationId }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">Access Technology</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.primaryAccessTechnology }}</div>
                                    </td>
                                </tr>                                
                            </table>
                        </div>
                    </div>
                    <div class="card mb-5">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">Address</h2>
                            <table class="table">
                                <tr>
                                    <td class="font-semibold" width="200">Road Number</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.address.roadNumber1 }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">Road Name</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.address.roadName }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">Road Type</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.address.roadTypeCode }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">Locality</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.address.localityName }}</div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">State</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>{{ asq.address.stateTerritoryCode }}</div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <div class="card mb-5">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">Location</h2>
                            <table class="table">
                                <tr>
                                    <td class="font-semibold">Latitude & Longitude</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>
                                            {{ asq.geo.latitude }}, {{ asq.geo.longitude }}
                                        </div>
                                    </td>
                                </tr>                            
                            </table>
                            <div class="border"><iframe width="100%" height="300" :src="'https://maps.google.com/maps?q='+asq.geo.latitude+','+asq.geo.longitude+'&t=&z=16&ie=UTF8&iwloc=&output=embed'" frameborder="0" scrolling="no" marginheight="0" marginwidth="0"></iframe></div>
                        </div>
                    </div>
                    <div class="card mb-5">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">CSA</h2>
                            <table class="table">
                                <tr>
                                    <td class="font-semibold" width="110">Name</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>
                                            {{ asq.csa.name }} - {{ asq.csa.id }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">State</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>
                                            {{ asq.csa.state }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-semibold">Status</td>
                                    <td>
                                        <div v-if="asqIsLoading" class="row-loading"></div>
                                        <div v-else>
                                            {{ asq.csa.active }}
                                        </div>
                                    </td>
                                </tr>                            
                            </table>
                        </div>
                    </div>
                    <div class="card mb-5" v-if="asq['Copper Pairs']">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">COPPER PAIRS AT THIS LOCATION</h2>
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th width="50"></th>
                                        <th width="130">Service Class</th>
                                        <th width="155">UNI-DSL ID</th>
                                        <th>PAIR STATUS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(cp, index) in asq['Copper Pairs']">
                                        <tr>
                                            <td>
                                                <radio 
                                                    v-model="createOrderForm.cpiId" 
                                                    :alt-value="index" 
                                                ></radio>
                                            </td>
                                            <td class="font-bold">{{ cp.ServiceabilityClass }}</td>
                                            <td class="font-bold">{{ index }}</td>
                                            <td class="font-bold">
                                                <span v-if="cp['NBN Service Status']">NBN: {{ cp['NBN Service Status'] }}</span>
                                                <span v-if="cp['Copper Pair Status']">Legacy: {{ cp['Copper Pair Status'] }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td></td>
                                            <td>Estimated Speed</td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td></td>
                                            <td>Traffic Class 4</td>
                                            <td class="overflow-visible">
                                                Downstream {{ cp['TC4 Downstream Lower Rate'] }}Mbps - {{ cp['TC4 Downstream Upper Rate'] }}Mbps<br>
                                                Upstream {{ cp['TC4 Upstream Lower Rate'] }}Mbps - {{ cp['TC4 Upstream Upper Rate'] }}Mbps
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="card mb-5" v-if="!asq['Copper Pairs'] || asq.ntd">
                        <div class="card-body">
                            <h2 class="uppercase mb-5 tracking-widest">NTDS AT THIS LOCATION</h2>
                            <template v-if="asq.ntd">
                                <div v-for="(ntd, index) in asq.ntd">
                                    <div class="flex items-center bg-gray-300 p-3 font-bold">
                                        <radio 
                                            v-model="createOrderForm.ntdId" 
                                            :alt-value="index" 
                                        >{{ index }}</radio>
                                    </div>
                                    <table class="table">
                                        <tr>
                                            <td class="font-semibold" width="200">Type</td>
                                            <td>
                                                <div v-if="asqIsLoading" class="row-loading"></div>
                                                <div v-else>
                                                    {{ ntd.NTDType || 'INTERNAL' }}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-if="ntd.NTDPowerType">
                                            <td class="font-semibold">Power Type</td>
                                            <td>
                                                <div v-if="asqIsLoading" class="row-loading"></div>
                                                <div v-else>
                                                    {{ ntd.NTDPowerType }}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-if="ntd.NTDLocation">
                                            <td class="font-semibold">Location</td>
                                            <td>
                                                <div v-if="asqIsLoading" class="row-loading"></div>
                                                <div v-else>
                                                    {{ ntd.NTDLocation }}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="font-semibold">Ports</td>
                                            <td>
                                                <div v-if="asqIsLoading" class="row-loading"></div>
                                                <div v-else>
                                                    <table>
                                                        <tr>
                                                            <td class="font-bold text-center">D1</td>
                                                            <td 
                                                                class="font-bold text-center"
                                                                v-if="ntd['port_1-UNI-D2']"
                                                            >D2</td>
                                                            <td 
                                                                class="font-bold text-center"
                                                                v-if="ntd['port_1-UNI-D3']"
                                                            >D3</td>
                                                            <td 
                                                                class="font-bold text-center"
                                                                v-if="ntd['port_1-UNI-D4']"
                                                            >D4</td>
                                                        </tr>
                                                        <tr>
                                                            <td class="text-center">
                                                                <img 
                                                                    src="@/assets/port-free-icon.jpg"
                                                                    v-if="ntd['port_1-UNI-D1'] == 'Free'"
                                                                >
                                                                <img 
                                                                    src="@/assets/port-used-icon.jpg"
                                                                    v-else
                                                                >
                                                            </td>
                                                            <td 
                                                                class="text-center"
                                                                v-if="ntd['port_1-UNI-D2']"
                                                            >
                                                                <img 
                                                                    src="@/assets/port-free-icon.jpg"
                                                                    v-if="ntd['port_1-UNI-D2'] == 'Free'"
                                                                >
                                                                <img 
                                                                    src="@/assets/port-used-icon.jpg"
                                                                    v-else
                                                                >
                                                            </td>
                                                            <td 
                                                                class="text-center"
                                                                v-if="ntd['port_1-UNI-D3']"
                                                            >
                                                                <img 
                                                                    src="@/assets/port-free-icon.jpg"
                                                                    v-if="ntd['port_1-UNI-D3'] == 'Free'"
                                                                >
                                                                <img 
                                                                    src="@/assets/port-used-icon.jpg"
                                                                    v-else
                                                                >
                                                            </td>
                                                            <td 
                                                                class="text-center"
                                                                v-if="ntd['port_1-UNI-D4']"
                                                            >
                                                                <img 
                                                                    src="@/assets/port-free-icon.jpg"
                                                                    v-if="ntd['port_1-UNI-D4'] == 'Free'"
                                                                >
                                                                <img 
                                                                    src="@/assets/port-used-icon.jpg"
                                                                    v-else
                                                                >
                                                            </td>
                                                        </tr>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>                            
                                    </table>
                                </div>
                            </template>
                            <div v-else class="text-gray-500">No NTDs at this location</div>
                        </div>
                    </div>
                    <div class="card mb-5" v-if="!asq['Copper Pairs'] || asq.ntd">
                        <div class="card-body">
                            <h2 class="flex items-center uppercase mb-5 tracking-widest">
                                <radio 
                                    v-model="createOrderForm.ntdId" 
                                    alt-value="new" 
                                >NEW OR UNLISTED NTD</radio>
                            </h2>
                            <div>
                                A brand new or unlisted NTD will be used for this service. Installation charges may apply.
                            </div>
                        </div>
                    </div>
                    <div class="card mb-5" v-if="asq['Copper Pairs']">
                        <div class="card-body">
                            <h2 class="flex items-center uppercase mb-5 tracking-widest">
                                <radio 
                                    v-model="createOrderForm.cpiId" 
                                    alt-value="new" 
                                >NEW OR UNLISTED COPPER PAIR</radio>
                            </h2>
                            <div>
                                A brand new or unlisted copper pair will be used for this service. Installation charges may apply.
                            </div>
                        </div>
                    </div>
                    <div class="mb-5 bg-red-500 text-white p-4 rounded shadow" v-if="showNtdCpError">
                        <div v-if="asq['Copper Pairs']">Please select a copper pair.</div>
                        <div v-else>Please select an NTD.</div>
                    </div>
                    <div class="text-right">
                        <app-button 
                            class="btn btn-blue" @click="() => {
                                if (createOrderForm.ntdId || createOrderForm.cpiId) {
                                    showResultsModal = false;
                                    showCreateOrderModal = true;
                                } else {
                                    showNtdCpError = true;
                                }
                            }"
                        >Create Order</app-button>
                    </div>
                </template>
                <div v-else class="bg-red-500 p-4 text-white rounded">{{ asqError }}</div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateOrderModal"
            width="lg:w-1/2"
            bg="bg-gray-200"
            @close="() => {
                asqError = null;
                createOrderForm.reset();
                $store.commit('setShowModal', true);
            }"
        >
            <template slot="header">Create Order</template>
            <template slot="body">
                <div class="mb-5">To create a new order, please fill in the form below.</div>
                <div class="card mb-5">
                    <div class="card-body">
                        <h2 class="uppercase mb-5 tracking-widest">Service</h2>
                        <table class="table">
                            <tr>
                                <td class="font-semibold" width="200">Service Class</td>
                                <td>
                                    <div>{{ asq.serviceClassNumber }} - {{ asq.serviceClassDesc }}</div>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-semibold">NBN Location ID</td>
                                <td>
                                    <div>{{ asq.nbnLocationId }}</div>
                                </td>
                            </tr>
                            <tr>
                                <td class="font-semibold">Service Address</td>
                                <td>
                                    <div>{{ `${asq.address.roadNumber1} ${asq.address.roadName} ${asq.address.roadTypeCode} ${asq.address.localityName} ${asq.address.stateTerritoryCode} ${asq.address.postcode}` }}</div>
                                </td>
                            </tr>
                            <tr v-if="asq['Copper Pairs']">
                                <td class="font-semibold">Central Splitter</td>
                                <td>
                                    <div class="flex items-center">
                                        <checkbox 
                                            v-model="createOrderForm.installationCentralSplitter"
                                            check-value="Yes"
                                            uncheck-value="No"
                                        ></checkbox>
                                        <div class="ml-2 text-gray-600 text-sm">(Check this box if a Central Splitter is required for the end user)</div>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
                <div class="card mb-5">
                    <div class="card-body">
                        <h2 class="uppercase mb-5 tracking-widest">ACCESS SEEKER INFORMATION</h2>
                        <div class="flex items-center mt-4">
                            <div class="font-semibold mr-6">End User Type</div>
                            <radio 
                                v-model="createOrderForm.endUserType" 
                                alt-value="Residential"
                            >Residential</radio>
                            <radio 
                                v-model="createOrderForm.endUserType" 
                                alt-value="Business"
                                class="ml-5"
                            >Business</radio>
                        </div>
                        <app-input 
                            v-model="createOrderForm.accessSeekerRef"
                            placeholder="Your Reference (optional)"
                        />                        
                        <div class="mt-4" v-if="createOrderForm.endUserType == 'Business'">
                            <app-input 
                                v-model="createOrderForm.businessName"
                                placeholder="Business Name"
                                required
                            />
                            <app-input 
                                v-model="createOrderForm.tradingName"
                                placeholder="Trading As (Optional)"
                            />
                            <app-input 
                                v-model="createOrderForm.abn"
                                placeholder="ABN"
                                required
                            />
                        </div>
                    </div>
                </div>
                <div class="card mb-5">
                    <div class="card-body">
                        <h2 class="uppercase mb-5 tracking-widest">Service Speed</h2>
                        <app-select
                            v-model="createOrderForm.tc4DownloadSpeed"
                            placeholder="Download Speed"
                            value-key="value"
                            label-key="label"
                            :error="createOrderForm.errors.get('tc4DownloadSpeed')"
                            @updated="createOrderForm.errors.clear('tc4DownloadSpeed')"
                            :options="[
                                {value: 12, label: '12Mbps'},
                                {value: 25, label: '25Mbps'},
                                {value: 50, label: '25-50Mbps'},
                                {value: 100, label: '25-100Mbps'}
                            ]"
                            class="mb-4"
                            required
                        />
                        <app-select
                            v-model="createOrderForm.tc4UploadSpeed"
                            placeholder="Upload Speed"
                            value-key="value"
                            label-key="label"
                            :error="createOrderForm.errors.get('tc4UploadSpeed')"
                            @updated="createOrderForm.errors.clear('tc4UploadSpeed')"
                            :options="[
                                {value: 1, label: '1Mbps'},
                                {value: 5, label: '5Mbps'},
                                {value: 10, label: '5-10Mbps'},
                                {value: 20, label: '5-20Mbps'},
                                {value: 40, label: '5-40Mbps'}
                            ]"
                            required
                        />
                    </div>
                </div>
                <div class="card mb-5" v-if="asq['Copper Pairs']">
                    <div class="card-body">
                        <h2 class="uppercase mb-5 tracking-widest">COPPER PAIRS AT THIS LOCATION</h2>
                        <table class="table">
                            <thead>
                                <tr>
                                    <th width="50"></th>
                                    <th width="130">Service Class</th>
                                    <th width="155">UNI-DSL ID</th>
                                    <th>PAIR STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(cp, index) in asq['Copper Pairs']">
                                    <tr>
                                        <td>
                                            <radio 
                                                v-model="createOrderForm.cpiId" 
                                                :alt-value="index" 
                                            ></radio>
                                        </td>
                                        <td class="font-bold">{{ cp.ServiceabilityClass }}</td>
                                        <td class="font-bold">{{ index }}</td>
                                        <td class="font-bold">
                                            <span v-if="cp['NBN Service Status']">NBN: {{ cp['NBN Service Status'] }}</span>
                                            <span v-if="cp['Copper Pair Status']">Legacy: {{ cp['Copper Pair Status'] }}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td>Estimated Speed</td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td>Traffic Class 4</td>
                                        <td class="overflow-visible">
                                            Downstream {{ cp['TC4 Downstream Lower Rate'] }}Mbps - {{ cp['TC4 Downstream Upper Rate'] }}Mbps<br>
                                            Upstream {{ cp['TC4 Upstream Lower Rate'] }}Mbps - {{ cp['TC4 Upstream Upper Rate'] }}Mbps
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="card mb-5" v-if="asq['Copper Pairs']">
                    <div class="card-body">
                        <h2 class="flex items-center uppercase mb-5 tracking-widest">
                            <radio 
                                v-model="createOrderForm.cpiId" 
                                alt-value="new" 
                            >NEW OR UNLISTED COPPER PAIR</radio>
                        </h2>
                        <div>
                            A brand new or unlisted copper pair will be used for this service. Installation charges may apply.
                        </div>
                    </div>
                </div>
                <div class="text-right">
                    <app-button 
                        class="btn btn-blue" 
                        :loading="createOrderFormIsLoading"
                        @click="orderFeasibilityCheck"
                    >Submit Order</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAppointmentAvailabilityModal"
            @close="() => {
                availableAppointmentForm.reset();
            }"
        >
            <template slot="header">Check Appointment Availability</template>
            <template slot="body">
                <app-select 
                    v-model="availableAppointmentForm.demandType"
                    placeholder="Demand Type"
                    :options="['Standard Install', 'Non EU Jumper Only', 'Service Restoration']"
                    :error="availableAppointmentForm.errors.get('demandType')"
                    @updated="availableAppointmentForm.errors.clear('demandType')"
                    required
                />
                <app-select 
                    v-model="availableAppointmentForm.slotType"
                    placeholder="Slot Type"
                    :options="['AM', 'PM']"
                    :error="availableAppointmentForm.errors.get('slotType')"
                    @updated="availableAppointmentForm.errors.clear('slotType')"
                    required
                />                
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showAppointmentAvailabilityModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="checkingAppointmentAvailability"
                        @click="checkAvailableAppointment"
                    >Check</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAvailableAppointmentsModal"
        >
            <template slot="header">Available Appointment Schedules</template>
            <template slot="body">
                <div
                    v-for="(a, key) in appointments"
                    class="px-3 py-2 flex items-center justify-between mb-2 rounded border"
                >
                    <div>{{ a.appointmentFormatted }}</div>
                    <app-button
                        class="btn-blue"
                        @click="selectAppointment(a)"
                    >Select</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showReserveAppointmentModal"
            @close="() => {
                createOrderForm.reset();
                createOrderForm.errors.clear();
            }"
        >
            <template slot="header">Reserve Appointment</template>
            <template slot="body">
                <app-input
                    v-model="appointment.appointmentFormatted"
                    placeholder="Date"
                    disable
                />
                <app-input
                    v-model="createOrderForm.contactName"
                    placeholder="Contact Name"
                    :error="createOrderForm.errors.get('contactName')"
                    @updated="createOrderForm.errors.clear('contactName')"
                    required
                    focus
                />
                <app-input
                    v-model="createOrderForm.contactPhone"
                    placeholder="Contact Phone"
                    :error="createOrderForm.errors.get('contactPhone')"
                    @updated="createOrderForm.errors.clear('contactPhone')"
                    required
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showReserveAppointmentModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="createOrderFormIsLoading"
                        @click="submitOrder"
                    >Submit Order</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show="show && path === 'sq-by-location-id'"
            @close="close"
        >
            <template slot="header">SQ By Location ID</template>
            <template slot="body">
                <div class="mb-5">To complete a service qualification, please provide a location ID below. If you know of the CSN/ULL ID for the location below, please provide it as part of the location ID search.</div>
                <app-input
                    v-model="nbnLocationId"
                    placeholder="NBN Location ID"
                    focus
                />
                <app-input
                    v-model="searchAddressForm.potsInterconnectId"
                    placeholder="CSN/ULL ID"
                    :error="searchAddressForm.errors.get('potsInterconnectId')"
                    @updated="searchAddressForm.errors.clear('potsInterconnectId')"
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="close"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        @click="check({nbnLocationId})"
                    >Check</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Form from '@/helpers/Form';
    import { mapGetters } from 'vuex';
    import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
    import '@/css/datepicker.css';

    export default {
        props: {
            show: {
                type: Boolean,
                required: true,
            },
            path: {
                type: String,
                required: true,
            }
        },
        components: {
            LeftArrowIcon: () => import('@/icons/ArrowLeft'),
            VueCtkDateTimePicker,
        },
        data() {
            return {
                checkingAppointmentAvailability: false,
                showAppointmentAvailabilityModal: false,
                showAvailableAppointmentsModal: false,
                showReserveAppointmentModal: false,
                nbnLocationId: null,
                searchAddressForm: new Form({
                    address: null,
                    potsInterconnectId: null
                }),
                availableAppointmentForm: new Form({
                    nbnLocationId: null,
                    demandType: null,
                    slotType: null,
                }),
                advancedSearchForm: new Form({
                    roadnumber1: null,
                    roadnumber12: null,
                    roadname: null,
                    roadtypecode: null,
                    roadsuffixcode: null,
                    leveltypecode: null,
                    levelnumber: null,
                    secondarycomplexname: null,
                    complexroadenumber1: null,
                    complexroadenumber2: null,
                    complexroadname: null,
                    complexroadtypecode: null,
                    complexroadsuffixcode: null,
                    stateterritorycode: null,
                    unittypecode: null,
                    unitnumber: null,
                    addresssitename: null,
                    lotnumber: null,
                    plannumber: null,
                    localityname: null,
                    postcode: null,
                }),
                createOrderForm: new Form({
                    sqId: null,
                    nbnLocationId: null,
                    ntdId: null,
                    cpiId: null,
                    installationCentralSplitter: 'No',
                    accessSeekerRef: null,
                    endUserType: 'Residential',
                    businessName: null,
                    tradingName: null,
                    abn: null,
                    tc4DownloadSpeed: null,
                    tc4UploadSpeed: null,
                    contactPhone: null,
                    contactName: null,
                    demandType: null,
                    timeSlot: null,
                    startTimeRaw: null,
                    endTimeRaw: null,
                    appointment: false,
                    potsInterconnectId: null,
                }),
                searchingAddress: false,
                showResultsModal: false,
                showCreateOrderModal: false,
                showNtdCpError: false,
                address: null,
                asqIsLoading: false,
                asqError: null,
                createOrderFormIsLoading: false,
                cantFindAddress: false,
                isComplex: false,
                advancedSearchFormIsLoading: false,
            }
        },
        computed: {
            ...mapGetters({
                addresses: 'wideband/address/items',
                asq: 'wideband/address/asq', // address service qualification
                appointments: 'wideband/appointments/items',
                appointment: 'wideband/appointments/selected',
            })
        },
        watch: {
            show(show) {
                if (show) {
                    this.nbnLocationId = null;
                }
            }
        },
        methods: {
            close() {
                this.$emit('update:show', false);
                this.$emit('close');
                this.$store.commit('wideband/address/setItems', []);
                this.cantFindAddress = false;
                this.searchAddressForm.reset();
            },
            searchAddress() {
                this.searchingAddress = true;
                this.searchAddressForm.errors.clear();
                this.$store.dispatch('wideband/address/search', this.searchAddressForm.payload())
                    .then(() => {
                        this.searchingAddress = false;
                    })
                    .catch(({ response }) => {
                        this.searchingAddress = false;
                        this.searchAddressForm.errors.record(response.data);
                    })
            },
            check(payload) {
                this.showResultsModal = true;
                this.address = payload.address || payload.formattedAddress;
                this.asqIsLoading = true;
                this.$emit('update:show', false);
                this.$emit('close');
                this.$store.dispatch('wideband/address/asq', {
                    locId: payload.nbnLocationId,
                    potsInterconnectId: this.searchAddressForm.potsInterconnectId
                })
                    .then(({ data }) => {
                        this.asqIsLoading = false;
                        this.createOrderForm.set({
                            sqId: data.sqId,
                            nbnLocationId: data.nbnLocationId,
                        });
                    })
                    .catch(({ response }) => {
                        this.asqIsLoading = false;
                        this.asqError = response.data.errors.error[0];
                    });
            },
            advancedSearch() {
                this.advancedSearchFormIsLoading = true;
                this.advancedSearchForm.errors.clear();
                this.searchAddressForm.reset();
                this.$store.dispatch('wideband/address/advanceSearch', this.advancedSearchForm.payload())
                    .then(({data}) => {
                        this.advancedSearchFormIsLoading = false;
                        this.cantFindAddress = false;
                    })
                    .catch(({ response }) => {
                        this.advancedSearchFormIsLoading = false;
                        this.advancedSearchForm.errors.record(response.data);

                        if (this.advancedSearchForm.errors.has('results')) {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: this.advancedSearchForm.errors.get('results'),
                            });
                        }
                    })
            },
            orderFeasibilityCheck() {
                // If access technology is equal to HFC appointment is required I guess.                
                if (this.asq.serviceClassNumber === 22) {
                    this.showAppointmentAvailabilityModal = true;
                    this.availableAppointmentForm.set({
                        nbnLocationId: this.createOrderForm.nbnLocationId,
                    });

                    return;
                }

                this.createOrderFormIsLoading = true;
                this.createOrderForm.errors.clear();
                this.$store.dispatch('wideband/orders/feasibility', this.createOrderForm.payload())
                    .then(({ data }) => {
                        this.createOrderFormIsLoading = false;

                        const statusNeedAppointment = ['Feasible - Delayed', 'Feasible - Appointment Required'];
                        if (statusNeedAppointment.includes(data.interactionStatus)) {
                            this.showAppointmentAvailabilityModal = true;
                            this.availableAppointmentForm.set({
                                nbnLocationId: this.createOrderForm.nbnLocationId,
                                demandType: data.appointment['Demand Type'],                                
                            });

                            return;
                        }

                        if (data.interactionStatus === 'Rejected') {
                            this.$swal.fire({
                                type: 'error',
                                title: 'Oops!',
                                text: data.sqnote['Standard Description'],
                            });

                            return;
                        }

                        this.submitOrder();
                    })
                    .catch(({ response }) => {
                        this.createOrderFormIsLoading = false;
                        this.createOrderForm.errors.record(response.data);
                    })
            },
            checkAvailableAppointment() {
                this.checkingAppointmentAvailability = true;
                this.$store.dispatch('wideband/appointments/check', this.availableAppointmentForm.payload())
                    .then(() => {
                        this.checkingAppointmentAvailability = false;
                        this.showAvailableAppointmentsModal = true;
                    })
                    .catch(({ response }) => {
                        this.checkingAppointmentAvailability = false;
                        this.availableAppointmentForm.errors.record(response.data);
                    })
            },
            selectAppointment(appointment) {
                this.$store.commit('wideband/appointments/setSelected', appointment);
                this.createOrderForm.set({
                    timeSlot: appointment.timeSlot,
                    startTimeRaw: appointment.startTimeRaw,
                    endTimeRaw: appointment.endTimeRaw,
                    demandType: this.availableAppointmentForm.demandType,
                    appointment: true,
                });
                this.showReserveAppointmentModal = true;
                return;
            },
            submitOrder() {
                this.createOrderFormIsLoading = true;
                this.createOrderForm.errors.clear();
                this.createOrderForm.set({
                    potsInterconnectId: this.searchAddressForm.potsInterconnectId,
                });
                this.$store.dispatch('wideband/orders/store', this.createOrderForm.payload())
                    .then(({ data }) => {
                        this.createOrderFormIsLoading = false;
                        this.showCreateOrderModal = false;
                        this.showAppointmentAvailabilityModal = false;
                        this.showAvailableAppointmentsModal = false;
                        this.showReserveAppointmentModal = false;
                        this.showResultsModal = false;
                        this.close();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Order was successfully created.'
                        })
                    })
                    .catch(({ response }) => {
                        this.createOrderFormIsLoading = false;
                        this.createOrderForm.errors.record(response.data);

                        this.$swal.fire({
                            type: 'error',
                            title: 'Oops...',
                            text: response.data.message
                        })
                    });
            }
        }
    }
</script>
